import client from "@/api/client"
import { baseResponseSchema } from "@/api/schemas/shared"
import { z } from "zod"

export const businessPlanSchema = z
  .object({
    _id: z.string(),
    name: z.string(),
    tier: z.enum(["STARTER", "PREMIUM"]),
    price: z.number(),
    duration: z.number().optional(),
    durationUnit: z.string().optional(),
    features: z.array(z.string()).optional(),
    description: z.string().optional(),
    status: z.string().optional(),
    activeSubscriptions: z.number().optional(),
  })
  .passthrough()

export type BusinessPlan = z.infer<typeof businessPlanSchema>

const businessPlansResponseSchema = baseResponseSchema.extend({
  data: z.array(businessPlanSchema),
})

const businessPlanResponseSchema = baseResponseSchema.extend({
  data: businessPlanSchema.nullable(),
})

const subscriptionResponseSchema = baseResponseSchema.extend({
  data: z
    .object({
      businessPlan: businessPlanSchema.optional().nullable(),
      subscriptionStatus: z.string().optional(),
      subscriptionExpiresAt: z.string().optional().nullable(),
      approvalStatus: z.string().optional(),
    })
    .passthrough()
    .optional()
    .nullable(),
})

const paySubscriptionResponseSchema = baseResponseSchema.extend({
  data: z
    .object({
      url: z.string().optional(),
      accessCode: z.string().optional(),
      reference: z.string().optional(),
      paystackReference: z.string().optional(),
      activated: z.boolean().optional(),
    })
    .passthrough()
    .optional()
    .nullable(),
})

export const getBusinessPlans = async () => {
  const response = await client.get("/api/v1/business/plan")
  return businessPlansResponseSchema.parse(response.data)
}

export const getBusinessPlanById = async (planId: string) => {
  const response = await client.get(`/api/v1/business/plan/${planId}`)
  return businessPlanResponseSchema.parse(response.data)
}

export type UpdateBusinessPlanPayload = {
  name?: string
  price?: number
  duration?: number
  durationUnit?: "DAY" | "WEEK" | "MONTH" | "YEAR"
  description?: string
  features?: string[]
  status?: "ACTIVE" | "SUSPENDED"
}

export const updateBusinessPlan = async (planId: string, payload: UpdateBusinessPlanPayload) => {
  const response = await client.patch(`/api/v1/business/plan/${planId}`, payload)
  return businessPlanResponseSchema.parse(response.data)
}

export const getBusinessSubscription = async (businessId: string) => {
  const response = await client.get(`/api/v1/business/${businessId}/subscription`)
  return subscriptionResponseSchema.parse(response.data)
}

export const payBusinessSubscription = async (businessId: string) => {
  const response = await client.post(`/api/v1/business/${businessId}/subscription/pay`)
  return paySubscriptionResponseSchema.parse(response.data)
}

export const upgradeBusinessSubscription = async (
  businessId: string,
  businessPlanId: string
) => {
  const response = await client.post(`/api/v1/business/${businessId}/subscription/upgrade`, {
    businessPlanId,
  })
  return baseResponseSchema.parse(response.data)
}

export const confirmSubscriptionPayment = async (reference: string) => {
  const response = await client.post("/api/v1/payment/confirmPayment", {
    reference,
  })
  return response.data
}
